import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Heading from "components/Typography/Header";
import Button from "components/Button";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import swal from 'sweetalert';
import { useUUID } from "hooks/useUUID";
import Popup from "components/Popup";
import { createTransfer, reset } from "redux/reseller/transfers/transfersSlice";
import { useCustomSettings } from 'hooks/useCustomSettings';
import moment from "moment";
import "moment/locale/tr";
moment.locale("tr");

const TransferCreateContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [trigger, setTrigger] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [lastFormValues, setLastFormValues] = useState(null);
  const [lastTransferData, setLastTransferData] = useState(null);
  const uuid = useUUID(trigger);

  const { isError, isLoading, isSuccess, message, transfer } = useSelector(
    (state) => state.resellerTransfers
  );
  const profile = useSelector((state) => state.resellerProfile.profile);
  
  // Get settings data for commission calculations
  const { data: settingsData } = useSelector(state => state.resellerSettings);
  const keyTextMapping = {}; // Assuming this might be needed for the hook
  const internalTransferFeeFixed = useCustomSettings(settingsData, keyTextMapping, 'internal_transfer_fee_fixed');

  // Format monetary values with Turkish locale
  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return '0,00';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Calculate commission based on backend logic
  const calculateCommission = useMemo(() => {
    return (transferAmount) => {
      if (!transferAmount || !profile?.internal_transfer_commission_rate) {
        return { commissionAmount: 0, totalAmount: transferAmount || 0 };
      }

      const amount = parseFloat(transferAmount);
      const commissionRate = parseFloat(profile.internal_transfer_commission_rate);
      const fixedFee = parseFloat(internalTransferFeeFixed.value) || 0;

      // Backend logic: commission_amount = amount / (1 - commission_rate/100) - amount
      let commissionAmount = amount / (1 - commissionRate / 100) - amount;
      commissionAmount = Math.max(commissionAmount, 0.01);
      commissionAmount += fixedFee;
      
      const totalAmount = amount + commissionAmount;
      
      return {
        commissionAmount: parseFloat(commissionAmount.toFixed(2)),
        totalAmount: parseFloat(totalAmount.toFixed(2))
      };
    };
  }, [profile?.internal_transfer_commission_rate, internalTransferFeeFixed]);

  const transferForm = Yup.object().shape({
    targetPhone: Yup.string()
      .required("Telefon numarası gerekli")
      .matches(/^[0-9]+$/, "Sadece rakam girebilirsiniz"),
    amount: Yup.number()
      .required("Transfer miktarı gerekli")
      .positive("Transfer miktarı pozitif olmalıdır")
  });

  useEffect(() => {
    console.log(`isError: ${isError}, isLoading: ${isLoading}, isSuccess: ${isSuccess}, message: ${message}, transfer: ${JSON.stringify(transfer)}`);
    if (isLoading){}
    else if (isError) {
      toast.error(message);
    } else if (transfer && transfer.need_confirm) {
      const el = document.createElement("div");
      el.innerHTML = transfer.message;
      swal({
        title: "Transfer Onayı",
        content: {
          element: el
        },
        icon: "info",
        buttons: ["İptal", "Onayla"]
      })
      .then(confirmed => {
        if (confirmed) {
          dispatch(
            createTransfer({
              target_phone: lastFormValues.targetPhone,
              amount_try: lastFormValues.amount,
              nonce: uuid,
              is_confirmed: true
            })
          );
        }
      });
    } else if (isSuccess) {
      setIsPopupOpen(true);
      setLastTransferData(transfer);
      if (lastFormValues && lastFormValues.resetForm) {
        lastFormValues.resetForm();
      }
      setTrigger((prev) => prev + 1);
    }
    return () => {
      dispatch(reset());
    };
  }, [isSuccess, isError, message, dispatch]);

  const handleSubmit = async (values, { resetForm }) => {
    setLastFormValues({ ...values, resetForm });
    
    dispatch(
      createTransfer({
        target_phone: values.targetPhone,
        amount_try: values.amount,
        nonce: uuid,
        is_confirmed: false
      })
    );
  };

  return (
    <div className="p-4">
      <Heading className="text-white-100" level={5}>
        Bayiye Transfer
      </Heading>
      <div className="p-4 bg-gray-900 my-4 rounded-xl min-h-screen">
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <div className="p-4 bg-black md:h-screen">
            <Formik
              initialValues={{
                targetPhone: "",
                amount: ""
              }}
              validationSchema={transferForm}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, setFieldValue, values }) => {
                const commissionInfo = calculateCommission(values.amount);
                const currentBalance = parseFloat(profile?.balance_try || 0);
                const finalBalance = currentBalance - commissionInfo.totalAmount;
                
                return (
                  <Form className="space-y-4">
                    <div className="text-white-100 font-medium mb-4">
                      <div>Hesabınızda Bulunan Bakiye: {formatMoney(profile?.balance_try)} TL</div>
                    </div>
                    <div className="">
                      <span className="text-white-100 font-medium">
                        Alıcı Bayi Telefon Numarası
                      </span>
                      <Field
                        name="targetPhone"
                        type="text"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none p-2 mt-2"
                      />
                      <ErrorMessage
                        name="targetPhone"
                        component="div"
                        className="text-red-500"
                      />
                    </div>

                    <div className="">
                      <span className="text-white-100 font-medium">
                        Transfer Miktarı (TL)
                      </span>
                      <Field
                        name="amount"
                        type="number"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none p-2 mt-2"
                      />
                      <ErrorMessage
                        name="amount"
                        component="div"
                        className="text-red-500"
                      />
                    </div>

                    {values.amount && parseFloat(values.amount) > 0 && (
                      <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                        <div className="text-white-100 font-medium text-sm">Transfer Özeti</div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between text-gray-300">
                            <span>Transfer Miktarı:</span>
                            <span>{formatMoney(values.amount)} TL</span>
                          </div>
                          <div className="flex justify-between text-gray-300">
                            <span>Komisyon ({profile?.internal_transfer_commission_rate}% + {internalTransferFeeFixed.value} TL):</span>
                            <span>{formatMoney(commissionInfo.commissionAmount)} TL</span>
                          </div>
                          <div className="border-t border-gray-600 pt-1">
                            <div className="flex justify-between text-white-100 font-medium">
                              <span>Toplam Ücret:</span>
                              <span>{formatMoney(commissionInfo.totalAmount)} TL</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      type="submit"
                      size="small"
                      loading={isLoading}
                      disabled={isLoading || (values.amount && finalBalance < 0)}
                      className="bg-primary"
                    >
                      Transfer Başlat
                    </Button>

                    {values.amount && parseFloat(values.amount) > 0 && (
                      <div className="flex flex-col space-y-2 mt-4">
                        <span className={`block text-sm font-medium ${finalBalance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          İşlem Sonu Bakiyeniz: {formatMoney(finalBalance)} TL
                        </span>
                        {finalBalance < 0 && (
                          <span className="text-red-400 text-xs">
                            Yetersiz bakiye! Transfer için {formatMoney(Math.abs(finalBalance))} TL eksik.
                          </span>
                        )}
                      </div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
      <Popup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div>
          <Heading className="text-primary text-center" level={5}>
            Başarılı
          </Heading>
          <p className="text-white-100 mt-4 text-xl font-semibold">
            Transfer Başarıyla Tamamlandı.
          </p>
        </div>
      </Popup>
    </div>
  );
};

export default TransferCreateContainer; 